import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useInvestigationSearch,
  useAddInvestigationAdvice,
  useInvestigationAdvice,
  useDeleteInvestigationAdviceItem,
} from "@/hooks/useInvestigationAdvice";
import { Search, Loader2, Beaker, Scan } from "lucide-react";
import { cn } from "@/lib/utils";
import { DismissibleChip } from "@/components/consultation/shared/DismissibleChip";

interface AddInvestigationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
}

export const AddInvestigationDialog = ({
  open,
  onOpenChange,
  appointmentId,
}: AddInvestigationDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [activeType, setActiveType] = useState<
    "lab_tests" | "imaging" | "others" | ""
  >("");

  const { data: searchResults, isLoading: isSearching } =
    useInvestigationSearch(debouncedSearch, activeType);
  const addAdviceMutation = useAddInvestigationAdvice(appointmentId);
  const deleteAdviceMutation = useDeleteInvestigationAdviceItem(appointmentId);
  const { data: adviceList } = useInvestigationAdvice(appointmentId);

  const activeAdvice = adviceList?.[0];
  const adviceItems = activeAdvice?.investigation_advice_items || [];

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setActiveType("");
    }
  }, [open]);

  const handleSelect = async (id: number) => {
    try {
      await addAdviceMutation.mutateAsync(id);
      // Don't close so user can add multiple items
    } catch (error) {
      console.error(error);
    }
  };

  // Group existing items by type for display
  const groupedAdviceItems = adviceItems.reduce(
    (acc, item) => {
      const type = item.item_type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    },
    {} as Record<string, typeof adviceItems>
  );

  // Filter search results to exclude already-added items
  const addedItemIds = new Set(
    adviceItems.map((item) => item.investigation_item)
  );
  const filteredResults = (searchResults || []).filter(
    (item) => !addedItemIds.has(item.id)
  );

  // Determine if we should show results (for "All" tab show when there's a search query)
  const showResults =
    activeType !== "" || (activeType === "" && debouncedSearch.length >= 2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="z-h2 text-foreground">
            Add Investigation Advice
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-3" />

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Already Added Section */}
          <div className="px-6 py-4 border-b border-border/60 shrink-0">
            <h3 className="z-label text-muted-foreground mb-3">
              Selected Investigations
            </h3>
            <div className="rounded-xl border border-border/60 p-3 max-h-[160px] overflow-y-auto scrollbar-thin">
              {adviceItems.length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(groupedAdviceItems).map(([type, items]) => (
                    <div key={type} className="space-y-2">
                      <p className="z-eyebrow text-muted-foreground">
                        {type.replace(/_/g, " ")}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {items.map((item) => (
                          <DismissibleChip
                            key={item.id}
                            label={item.item_name}
                            onRemove={() =>
                              deleteAdviceMutation.mutate(item.id)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 z-body text-muted-foreground">
                  No investigations added yet
                </div>
              )}
            </div>
          </div>

          {/* Search Section */}
          <div className="flex-1 flex flex-col min-h-0 bg-background">
            <div className="p-6 border-b border-border/60 bg-background z-10 space-y-3">
              {/* Type filter buttons */}
              <div className="flex gap-2 overflow-x-auto pb-1">
                <Button
                  variant={activeType === "" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveType("")}
                  className={cn(
                    "whitespace-nowrap",
                    activeType === ""
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground"
                  )}
                >
                  All
                </Button>
                <Button
                  variant={activeType === "lab_tests" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveType("lab_tests")}
                  className={cn(
                    "whitespace-nowrap",
                    activeType === "lab_tests"
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground"
                  )}
                >
                  <Beaker className="h-3 w-3 mr-1" />
                  Lab Tests
                </Button>
                <Button
                  variant={activeType === "imaging" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveType("imaging")}
                  className={cn(
                    "whitespace-nowrap",
                    activeType === "imaging"
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground"
                  )}
                >
                  <Scan className="h-3 w-3 mr-1" />
                  Imaging
                </Button>
                <Button
                  variant={activeType === "others" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveType("others")}
                  className={cn(
                    "whitespace-nowrap",
                    activeType === "others"
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground"
                  )}
                >
                  Other
                </Button>
              </div>

              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={
                    activeType !== ""
                      ? `Search ${activeType.replace(/_/g, " ")}...`
                      : "Search investigations (type 2+ characters)..."
                  }
                  className="pl-9 bg-muted/50 border-border/60 rounded-xl z-body"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                )}
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto scrollbar-thin p-6">
              {isSearching ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Searching...
                </div>
              ) : showResults && filteredResults.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {filteredResults.map((item) => (
                    <DismissibleChip
                      key={item.id}
                      label={item.name}
                      onAdd={() => handleSelect(item.id)}
                    />
                  ))}
                </div>
              ) : showResults && filteredResults.length === 0 ? (
                <div className="text-center py-8 z-body text-muted-foreground">
                  {searchQuery.length >= 2
                    ? `No investigations found matching "${searchQuery}"`
                    : `No ${activeType.replace(/_/g, " ")} investigations found`}
                </div>
              ) : (
                <div className="text-center py-8 z-body text-muted-foreground">
                  {activeType === ""
                    ? "Type to search or select a category to browse"
                    : "Loading..."}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
